import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, MailCheck } from 'lucide-react';
import { useApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ROLE_LABELS } from '@/lib/labels';
import { formatDate } from '@/lib/dates';
import { Button, Card, ErrorText } from '@/components/ui';

/**
 * `/invite/:token`, from an invitation email: who invited you where, and one
 * click to join. Only the invited address can accept; someone signed in with
 * another account is told so and can switch.
 */
export function InvitationPage() {
  const { token = '' } = useParams();
  const api = useApi();
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const invite = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api.invitation(token),
    retry: false,
  });
  const accept = useMutation({
    mutationFn: () => api.acceptInvitation(token),
    onSuccess: ({ orgId }) => {
      queryClient.invalidateQueries({ queryKey: ['myOrgs'] });
      navigate(`/organizations/${orgId}`, { replace: true });
    },
  });

  if (invite.isPending || loading)
    return <p className="text-center text-sm text-muted-foreground">Loading…</p>;
  if (invite.isError)
    return (
      <Shell>
        <h1 className="text-xl font-semibold">This invitation link isn&rsquo;t valid</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          It may have been replaced by a newer email. Check your inbox for the latest invitation, or
          ask the person who invited you to send it again.
        </p>
      </Shell>
    );

  const i = invite.data;
  const here = `/invite/${token}`;
  const signIn = (fresh: boolean) =>
    navigate(`/signin?next=${encodeURIComponent(here)}&email=${encodeURIComponent(i.email)}`, {
      replace: fresh,
    });
  const otherAccount = user && user.email.toLowerCase() !== i.email.toLowerCase();

  return (
    <Shell>
      <Building2 className="mx-auto size-10 text-primary" aria-hidden />
      <p className="mt-3 text-sm uppercase tracking-wide text-muted-foreground">
        {i.status === 'open' ? 'You’re invited' : 'Invitation'}
      </p>
      <h1 className="mt-1 text-2xl font-semibold">Join {i.orgName}</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {i.inviterName ?? 'An admin'} invited {i.email} to join as{' '}
        {ROLE_LABELS[i.role].toLowerCase()}
        {i.groupName ? ` in ${i.groupName}` : ''}.
      </p>
      {i.message && (
        <blockquote className="mt-4 rounded-[var(--radius)] border-l-4 border-primary bg-muted/50 p-3 text-left text-sm">
          {i.message}
        </blockquote>
      )}

      {i.status === 'open' ? (
        <div className="mt-6 space-y-3">
          {!user ? (
            <>
              <Button className="w-full" onClick={() => signIn(false)}>
                Sign in or sign up to accept
              </Button>
              <p className="text-xs text-muted-foreground">
                Use {i.email}. There&rsquo;s no password: we&rsquo;ll email you a one-time code.
              </p>
            </>
          ) : (
            <>
              {otherAccount && (
                <p className="rounded-[var(--radius)] bg-muted/60 p-3 text-left text-sm">
                  You&rsquo;re signed in as <span className="font-medium">{user.email}</span>. This
                  invitation is for <span className="font-medium">{i.email}</span>.
                </p>
              )}
              <Button
                className="w-full"
                disabled={accept.isPending}
                onClick={() => accept.mutate()}
              >
                <MailCheck className="size-4" aria-hidden /> Accept and join {i.orgName}
              </Button>
              {otherAccount && (
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={async () => {
                    await signOut();
                    signIn(true);
                  }}
                >
                  Sign in as {i.email}
                </Button>
              )}
              <ErrorText error={accept.error} />
            </>
          )}
          <p className="text-xs text-muted-foreground">Valid until {formatDate(i.expiresAt)}.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3 text-sm text-muted-foreground">
          {i.status === 'accepted' && (
            <>
              <p>This invitation has been accepted.</p>
              {user && (
                <Button className="w-full" onClick={() => navigate(`/organizations/${i.orgId}`)}>
                  Open {i.orgName}
                </Button>
              )}
            </>
          )}
          {i.status === 'expired' && (
            <p>This invitation expired on {formatDate(i.expiresAt)}. Ask for a new one.</p>
          )}
          {i.status === 'revoked' && <p>This invitation was withdrawn.</p>}
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto max-w-md">
      <Card className="text-center">{children}</Card>
    </div>
  );
}
